import { store } from "@/lib/store.ts";
import { ReadAllResponse, uuid } from "@/interfaces";
import { organization_user_status } from "@/models/organization-user.model";
import { Property } from "@/models/properties.model.ts";
import { requirePermission } from "@/lib/permissions";
import { organization_type } from "@/models/organization.model.ts";
import { api } from "@/lib/api";
import type { DocumentOwner, DocumentRecord, InviteSent, MembershipRole, MembershipStatus, OrganizationMember, SettableMemberStatus } from "@shyne/shared/api";
import { everyProperty, toProperty } from "@/repositories/property-records";
import { toMemberOrganization } from "@/repositories/organization-records";
import { expiresOnOf } from "@/lib/documents";
import { DocumentsRepository } from "@/repositories/documents.repository";

async function propertiesAssignedTo(customer: uuid, person: uuid): Promise<Property[]> {
  const properties = await everyProperty(api.properties.list, { actingOrganization: customer, customer, assignedTo: person, sort: "name", order: "asc" });
  return properties.map(toProperty);
}

export type TeamMember = OrganizationMember;

export interface TeamMemberDetail {
  member: TeamMember;
  properties: Property[];
}

export interface TeamMemberFilters {
  role?: MembershipRole;
  status?: MembershipStatus;
  order: "asc" | "desc";
}

export interface CurrentMembership {
  organizationId: uuid;
  role: MembershipRole;
  status: organization_user_status;
}

function activeOrganization() {
  const { activeOrgId, organizations } = store.getState().organization;
  if (!activeOrgId) throw new Error("No active organization");
  return { id: activeOrgId, type: organizations.find((organization) => organization.id === activeOrgId)?.type };
}

const memberDocuments = (person: uuid): DocumentOwner => ({ kind: "member", organization: activeOrganization().id, person });

export const TeamMembersRepository = {
  async list(page = 1, pageSize = 10, { role, status, order }: TeamMemberFilters = { order: "asc" }): Promise<ReadAllResponse<TeamMember>> {
    const { items, total } = await api.members.list(activeOrganization().id, { role, status, order, page, pageSize });
    return { data: items, count: total };
  },

  async invite(name: string, email: string, role: MembershipRole): Promise<InviteSent> {
    await requirePermission("team_members.invite");

    return api.invites.send(activeOrganization().id, { kind: "team", email, name, role });
  },

  async countActive(): Promise<number> {
    return (await api.members.list(activeOrganization().id, { status: "active", pageSize: 1 })).total;
  },

  async get(memberId: uuid): Promise<TeamMember> {
    return api.members.get(activeOrganization().id, memberId);
  },

  async getDetail(memberId: uuid): Promise<TeamMemberDetail> {
    const organization = activeOrganization();
    const member = await api.members.get(organization.id, memberId);
    return { member, properties: organization.type === organization_type.pmc ? await propertiesAssignedTo(organization.id, member.person) : [] };
  },

  async setRole(memberId: uuid, role: MembershipRole): Promise<TeamMember> {
    return api.members.setRole(activeOrganization().id, memberId, { role });
  },

  async setStatus(memberId: uuid, status: SettableMemberStatus): Promise<TeamMember> {
    return api.members.setStatus(activeOrganization().id, memberId, { status });
  },

  async uploadDocument(person: uuid, file: File, expiresAt?: string | Date): Promise<DocumentRecord> {
    return DocumentsRepository.upload(memberDocuments(person), file, expiresOnOf(expiresAt));
  },

  async deleteDocument(docId: string): Promise<void> {
    await DocumentsRepository.remove(docId);
  },

  async listDocuments(person: uuid, page = 1, pageSize = 10): Promise<ReadAllResponse<DocumentRecord>> {
    return DocumentsRepository.page(memberDocuments(person), page, pageSize);
  },

  async getCurrentMembership(): Promise<CurrentMembership> {
    const orgId = store.getState().organization.activeOrgId;
    if (!orgId) throw new Error("No active organization");

    const { items } = await api.myOrganizations();
    const current = items.find((organization) => organization.id === orgId);
    if (!current) throw new Error("Not a member of this organization");

    const { role, status } = toMemberOrganization(current);
    return { organizationId: orgId, role, status };
  },
};
